import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "lichtmalerin_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          title: product.title,
          subtitle: product.subtitle,
          price_eur: product.price_eur,
          shipping_eur: product.shipping_eur,
          image_base64: product.image_base64,
          quantity,
        },
      ];
    });
  };

  const remove = (product_id) =>
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));

  const setQty = (product_id, quantity) =>
    setItems((prev) =>
      prev
        .map((i) =>
          i.product_id === product_id ? { ...i, quantity: Math.max(1, quantity) } : i,
        )
        .filter((i) => i.quantity > 0),
    );

  const clear = () => setItems([]);

  const subtotal = items.reduce(
    (s, i) => s + Number(i.price_eur) * i.quantity,
    0,
  );
  const shipping = items.reduce(
    (s, i) => s + Number(i.shipping_eur || 0) * i.quantity,
    0,
  );
  const total = subtotal + shipping;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, add, remove, setQty, clear, subtotal, shipping, total, count }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
